import { AlertCircle, ArrowLeftRight, Loader2, Lock, RefreshCw } from "lucide-react";
import { useId } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import {
  AmountBreakdownList,
  type AmountBreakdownRow,
} from "@/shared/components/payments/AmountBreakdownList";
import { FeeBearerSwitch } from "@/shared/components/payments/FeeBearerSwitch";
import { useTaxLabel } from "@/shared/hooks/useTaxLabel";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { maskAmount, type MaskedAmount, maskForCurrency } from "@/shared/utils/amountMask";
import { compareDecimalStrings } from "@/shared/utils/decimal";

import type { NativeQuote } from "../../services/paymentsQuotes.service";
import { currencySymbol } from "../../utils/currencyDisplay";
import {
  amountCurrencyFor,
  formatReadableRate,
  isCrossCurrencySend,
  sendBreakdown,
  sendHasCost,
  type SendFeeMode,
  type SendRoute,
} from "../../utils/walletSend";
import { formatSendMoney, formatSendMoneyWithCode } from "./formatSendMoney";
import { QuoteValidity } from "./QuoteValidity";

export interface SendAmountFieldProps {
  route: SendRoute;
  amount: MaskedAmount;
  onAmountChange: (next: MaskedAmount) => void;
  feeMode: SendFeeMode;
  onFeeModeChange: (mode: SendFeeMode) => void;
  quote: NativeQuote | null;
  /** Shown dimmed while the next price arrives, so the figures do not blink on every keystroke. */
  lastQuote: NativeQuote | null;
  loading: boolean;
  /** Said on the field itself: a limit or the balance. */
  fieldError: string | null;
  /** The price could not be computed for another reason. */
  quoteError: string | null;
  limitHint: string | null;
  onRetry: () => void;
  autoFocus?: boolean;
  /** Set when a scanned QR fixes the amount: it is shown as a figure, not a field, and the cost goes on top. */
  lockedHint?: string | null;
}

/** The rate on a converted send, and how long it holds. */
function RateLine({ quote }: { quote: NativeQuote }) {
  const { t, activeLanguage } = useTranslations("dispersions");
  const rate = formatReadableRate(quote, activeLanguage);
  if (!rate) return null;
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-lg bg-muted/50 px-3 py-2 text-xs">
      <span className="flex items-center gap-1.5 text-muted-foreground">
        <ArrowLeftRight className="size-3.5 shrink-0" aria-hidden="true" />
        {t("send_rate_label")}
        <span className="font-mono font-semibold text-foreground tabular-nums">{rate}</span>
      </span>
      <QuoteValidity expiresAt={quote.expires_at} />
    </div>
  );
}

export function SendAmountField({
  route,
  amount,
  onAmountChange,
  feeMode,
  onFeeModeChange,
  quote,
  lastQuote,
  loading,
  fieldError,
  quoteError,
  limitHint,
  onRetry,
  autoFocus,
  lockedHint,
}: SendAmountFieldProps) {
  const { t, activeLanguage } = useTranslations("dispersions");
  const taxLabel = useTaxLabel();
  const inputId = useId();
  const hintId = useId();
  const hasHint = !!(fieldError || lockedHint || limitHint);
  const shown = amount.value == null ? null : (quote ?? lastQuote);
  const breakdown = shown ? sendBreakdown(shown) : null;
  const crossCurrency = isCrossCurrencySend(route);
  const senderPays = feeMode === "sender";
  const amountCurrency = amountCurrencyFor(route, feeMode);
  const symbol = currencySymbol(amountCurrency);
  // Two currencies on one list: each figure carries its code, since "$" alone is pesos and dollars at once.
  const inDestination = (value: string) =>
    crossCurrency
      ? formatSendMoneyWithCode(value, route.currency, activeLanguage)
      : formatSendMoney(value, route.currency, activeLanguage);
  const inOrigin = (value: string) =>
    crossCurrency
      ? formatSendMoneyWithCode(value, route.originCurrency, activeLanguage)
      : formatSendMoney(value, route.originCurrency, activeLanguage);
  // With nothing to pay there is nobody to assume it: the switch and the third row would only repeat the amount.
  const hasCost = !!breakdown && sendHasCost(breakdown);
  const label = !crossCurrency
    ? t("send_amount_label")
    : senderPays
      ? t("send_amount_label_arrives")
      : t("send_amount_label_debit");

  const costRows = (sign: string): AmountBreakdownRow[] =>
    !breakdown
      ? []
      : [
          ...(compareDecimalStrings(breakdown.cost, "0") > 0
            ? [{ key: "cost", label: t("send_row_cost"), value: `${sign} ${inDestination(breakdown.cost)}` }]
            : []),
          ...breakdown.taxes.map((tax) => ({
            key: `tax-${tax.code}`,
            label: taxLabel(tax),
            value: `${sign} ${inDestination(tax.amount)}`,
          })),
          ...(breakdown.conversion !== null && compareDecimalStrings(breakdown.conversion, "0") > 0
            ? [
                {
                  key: "conversion",
                  label: t("send_row_conversion"),
                  value: `${sign} ${inOrigin(breakdown.conversion)}`,
                },
              ]
            : []),
        ];

  const arrivesRow = (total: boolean): AmountBreakdownRow => ({
    key: "arrives",
    label: t("send_row_arrives"),
    value: inDestination(breakdown?.arrives ?? "0"),
    total,
  });
  const debitRow = (total: boolean): AmountBreakdownRow => ({
    key: "debit",
    label: t("send_row_debit"),
    value: inOrigin(breakdown?.debit ?? "0"),
    total,
  });

  const rows: AmountBreakdownRow[] = !breakdown
    ? []
    : !hasCost
      ? [
          { key: "cost", label: t("send_row_cost"), value: t("send_cost_free") },
          ...(crossCurrency ? [debitRow(false)] : []),
          arrivesRow(true),
        ]
      : senderPays
        ? [arrivesRow(false), ...costRows("+"), debitRow(true)]
        : [debitRow(false), ...costRows("−"), arrivesRow(true)];

  return (
    <section className="space-y-3">
      <label
        htmlFor={inputId}
        className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground"
      >
        {label}
        {loading && amount.value != null && <Loader2 className="size-3 animate-spin" aria-hidden="true" />}
      </label>
      {lockedHint ? (
        <output
          id={inputId}
          aria-describedby={hasHint ? hintId : undefined}
          className="flex items-baseline gap-2 font-mono text-3xl font-bold tracking-tight text-foreground tabular-nums"
        >
          <span aria-hidden="true" className="text-2xl text-muted-foreground">
            {symbol}
          </span>
          {amount.display}
          <span className="text-sm font-medium tracking-wider text-muted-foreground">{amountCurrency}</span>
        </output>
      ) : (
        <div
          className={cn(
            "rounded-xl border-2 bg-card px-4 py-3 transition-colors",
            fieldError
              ? "border-destructive shadow-[0_0_0_4px_color-mix(in_oklch,var(--destructive)_12%,transparent)]"
              : "border-primary shadow-[0_0_0_4px_color-mix(in_oklch,var(--primary)_12%,transparent)]",
          )}
        >
          <div className="flex items-baseline gap-2">
            {symbol && (
              <span aria-hidden="true" className="font-mono text-2xl font-bold text-muted-foreground">
                {symbol}
              </span>
            )}
            <input
              id={inputId}
              value={amount.display}
              onChange={(event) =>
                onAmountChange(maskAmount(event.target.value, maskForCurrency(amountCurrency)))
              }
              inputMode={maskForCurrency(amountCurrency).decimalSep ? "decimal" : "numeric"}
              placeholder="0"
              autoComplete="off"
              autoFocus={autoFocus}
              aria-invalid={!!fieldError}
              aria-describedby={hasHint ? hintId : undefined}
              className="w-full min-w-0 bg-transparent font-mono text-3xl font-bold tracking-tight text-foreground outline-none placeholder:text-muted-foreground/40"
            />
            <span className="shrink-0 font-mono text-sm font-medium tracking-wider text-muted-foreground">
              {amountCurrency}
            </span>
          </div>
        </div>
      )}
      {hasHint && (
        <div id={hintId}>
          {fieldError ? (
            <p role="alert" className="text-sm font-medium text-destructive">
              {fieldError}
            </p>
          ) : lockedHint ? (
            <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
              <Lock className="mt-px size-3.5 shrink-0" aria-hidden="true" />
              {lockedHint}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">{limitHint}</p>
          )}
        </div>
      )}

      {hasCost && !lockedHint && (
        <FeeBearerSwitch
          label={t("send_fee_switch")}
          hint={
            crossCurrency
              ? senderPays
                ? t("send_fee_on_hint_fx", { currency: route.currency })
                : t("send_fee_off_hint_fx", { currency: route.originCurrency })
              : senderPays
                ? t("send_fee_on_hint")
                : t("send_fee_off_hint")
          }
          checked={senderPays}
          onCheckedChange={(checked) => onFeeModeChange(checked ? "sender" : "recipient")}
        />
      )}

      {quoteError ? (
        <ColocaAlert
          variant="destructive"
          icon={AlertCircle}
          text={quoteError}
          actions={
            <ColocaButton size="sm" variant="default" icon={RefreshCw} onClick={onRetry}>
              {t("button_retry")}
            </ColocaButton>
          }
        />
      ) : breakdown && shown ? (
        <div className="space-y-2">
          {crossCurrency && <RateLine quote={shown} />}
          <AmountBreakdownList rows={rows} loading={loading} />
        </div>
      ) : null}
    </section>
  );
}
